import React, { useEffect, useMemo, useState } from 'react';
import API from '../lib/api';
import AccessRequestsPanel from '../components/AccessRequestsPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, UserCheck, UserX, KeyRound, AlertCircle } from 'lucide-react';
import { formatIdentity } from '../lib/identity';

const ROLES = [
  { value: 'finance_admin',    label: 'Finance Admin' },
  { value: 'finance_reviewer', label: 'Finance Reviewer' },
  { value: 'cardholder',       label: 'Cardholder' },
  { value: 'auditor',          label: 'Auditor' },
];

const ROLE_BADGE = {
  finance_admin:    'bg-blue-100 text-blue-700',
  finance_reviewer: 'bg-amber-100 text-amber-700',
  cardholder:       'bg-teal-100 text-teal-700',
  auditor:          'bg-slate-100 text-slate-600',
  pending:          'bg-rose-100 text-rose-700',
};

const ROLE_LABELS = {
  ...Object.fromEntries(ROLES.map(r => [r.value, r.label])),
  pending: 'Pending activation',
};

function UserDialog({ open, onClose, onSave, initial, cardholders }) {
  const isEdit = !!initial;
  const isSSO  = initial?.authMethod === 'alchemy';
  const wasPending = initial?.role === 'pending';
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'cardholder', cardholder_id:'' });
  useEffect(() => {
    if (!open) return;
    if (initial) {
      // For pending SSO users, force the admin to make an explicit choice
      // rather than silently defaulting to 'cardholder'.
      setForm({
        name: initial.name || '',
        email: initial.email || '',
        password: '',
        role: initial.role === 'pending' ? '' : initial.role,
        cardholder_id: initial.cardholder_id || '',
      });
    } else {
      setForm({ name:'', email:'', password:'', role:'cardholder', cardholder_id:'' });
    }
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = () => {
    if (isEdit && !form.role) {
      toast.error('Please choose a role before saving.');
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? (wasPending ? `Activate ${initial.name || 'user'}` : 'Edit User')
              : 'Create User'}
          </DialogTitle>
        </DialogHeader>
        {wasPending && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            This user signed in via Alchemy SSO. Assign a role to grant them access to the workbench.
          </div>
        )}
        {isSSO && initial?.masterPersKey && (
          <div className="text-[11px] text-slate-500">
            <span className="text-slate-400">masterPersKey:</span> {initial.masterPersKey}
          </div>
        )}
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Full Name *</Label>
            <Input value={form.name} onChange={e=>set('name',e.target.value)}
                   className="mt-1 h-8 text-sm" data-testid="user-name-input"
                   disabled={isSSO} />
            {isSSO && (
              <p className="text-[10px] text-slate-400 mt-1">
                Name is sourced from the CRM and refreshed on each sign-in.
              </p>
            )}
          </div>
          {!isEdit && (
            <div><Label className="text-xs">Email Address *</Label><Input type="email" value={form.email} onChange={e=>set('email',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          )}
          {!isEdit && (
            <div><Label className="text-xs">Initial Password * (min 12 chars, 3 of 4 character classes)</Label><Input type="password" value={form.password} onChange={e=>set('password',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          )}
          <div>
            <Label className="text-xs">Role *</Label>
            <Select value={form.role} onValueChange={v => set('role', v)}>
              <SelectTrigger className="mt-1 h-8 text-sm" data-testid="user-role-select">
                <SelectValue placeholder="Select a role…" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role === 'cardholder' && (
            <div>
              <Label className="text-xs">Link to Cardholder Profile (optional)</Label>
              <Select value={form.cardholder_id} onValueChange={v => set('cardholder_id', v)}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select cardholder…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="">None</SelectItem>
                  {cardholders.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Links this login account to a cardholder profile for transaction isolation.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} data-testid="user-save-btn">
            {wasPending ? 'Activate' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onClose, onSave, userName }) {
  const [pw, setPw] = useState('');
  useEffect(() => { if (open) setPw(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader><DialogTitle>Reset Password — {userName}</DialogTitle></DialogHeader>
        <div className="py-2">
          <Label className="text-xs">New Password (min 12 chars, 3 of 4 character classes)</Label>
          <Input type="password" value={pw} onChange={e => setPw(e.target.value)} className="mt-1 h-8 text-sm" />
          <p className="text-xs text-slate-400 mt-1">User will be required to change this on next login.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(pw)}>Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const [users, setUsers]         = useState([]);
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all'); // 'all' | 'pending'

  const load = async () => {
    setLoading(true);
    try {
      const [ur, chr] = await Promise.all([API.get('/users'), API.get('/cardholders')]);
      setUsers(ur.data); setCardholders(chr.data);
    } catch(e) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) {
        await API.put(`/users/${editing.id}`, { name: form.name, role: form.role, cardholder_id: form.cardholder_id || null });
        toast.success(editing.role === 'pending' ? 'User activated.' : 'User updated');
      } else {
        await API.post('/users', form);
        toast.success('User created. They must change their password on first login.');
      }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const resetPassword = async (pw) => {
    try {
      await API.post(`/users/${resetTarget.id}/reset-password`, { new_password: pw });
      toast.success('Password reset. User must change on next login.');
      setResetTarget(null);
    } catch(e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const toggleStatus = async () => {
    try {
      await API.put(`/users/${toggleTarget.id}/status`, null, { params: { is_active: !toggleTarget.is_active } });
      toast.success(toggleTarget.is_active ? 'User disabled' : 'User enabled');
      setToggleTarget(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const chMap = Object.fromEntries(cardholders.map(c => [c.id, c.name]));
  const pendingCount = useMemo(() => users.filter(u => u.role === 'pending').length, [users]);
  const filtered = users
    .filter(u => filter === 'all' ? true : u.role === 'pending')
    .filter(u => !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.masterPersKey?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">User Management</h1>
          <p className="text-sm text-slate-500">Create and manage system user accounts and roles.</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 w-44 text-sm" />
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-user-btn">
            <Plus size={14} className="mr-1" /> New User
          </Button>
        </div>
      </div>

      <AccessRequestsPanel />

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertCircle size={16} />
            <span>
              <strong>{pendingCount}</strong> user{pendingCount === 1 ? '' : 's'} awaiting role assignment.
              These users have signed in via Alchemy SSO but cannot use the workbench until you activate them.
            </span>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}
                  className="h-7 text-xs">
            {filter === 'pending' ? 'Show all users' : 'Show pending only'}
          </Button>
        </div>
      )}

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="users-table">
          <thead><tr>
            <th>Name</th><th>Identifier</th><th>Auth</th><th>Role</th><th>Cardholder Link</th>
            <th>Status</th><th>Must Change PW</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">No users found</td></tr>
            ) : filtered.map(u => {
              const isSSO = u.authMethod === 'alchemy';
              return (
                <tr key={u.id} className={u.role === 'pending' ? 'bg-amber-50/40' : ''}>
                  <td className="font-medium">{u.name}</td>
                  <td className="text-slate-500 text-xs leading-tight">
                    {isSSO ? (
                      <div className="font-mono">{formatIdentity(u)}</div>
                    ) : (
                      <div>{u.email}</div>
                    )}
                  </td>
                  <td>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isSSO ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                      {isSSO ? 'SSO' : 'Local'}
                    </span>
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE[u.role] || 'bg-slate-100'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500">{u.cardholder_id ? (chMap[u.cardholder_id] || u.cardholder_id) : '—'}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${u.is_active !== false ? 'chip-approved' : 'chip-rejected'}`}>
                      {u.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="text-xs">{u.must_change_password ? <span className="text-amber-600 font-medium">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing(u); setDialogOpen(true); }}
                        className="p-1 text-slate-400 hover:text-emerald-600"
                        title={u.role === 'pending' ? 'Activate user' : 'Edit'}>
                        <Pencil size={13} />
                      </button>
                      {!isSSO && (
                        <button onClick={() => setResetTarget(u)}
                          className="p-1 text-slate-400 hover:text-amber-600" title="Reset password">
                          <KeyRound size={13} />
                        </button>
                      )}
                      <button onClick={() => setToggleTarget(u)}
                        className={`p-1 ${u.is_active !== false ? 'text-slate-400 hover:text-rose-600' : 'text-slate-400 hover:text-emerald-600'}`}
                        title={u.is_active !== false ? 'Disable user' : 'Enable user'}>
                        {u.is_active !== false ? <UserX size={13} /> : <UserCheck size={13} />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
        <strong>Security note:</strong> Local users must change their password on first login. SSO users authenticate
        via Alchemy and don't have a local password — they appear here in <em>Pending activation</em> until you assign
        a role.
      </div>

      <UserDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={save} initial={editing} cardholders={cardholders} />
      <ResetPasswordDialog open={!!resetTarget} onClose={() => setResetTarget(null)}
        onSave={resetPassword} userName={resetTarget?.name} />
      <AlertDialog open={!!toggleTarget} onOpenChange={() => setToggleTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{toggleTarget?.is_active !== false ? 'Disable' : 'Enable'} {toggleTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {toggleTarget?.is_active !== false
                ? 'This user will no longer be able to log in.'
                : 'This user will be able to log in again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={toggleStatus}
              className={toggleTarget?.is_active !== false ? 'bg-rose-600' : 'bg-emerald-600'}>
              {toggleTarget?.is_active !== false ? 'Disable' : 'Enable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
